import React, { useState, useEffect, useRef } from "react";
import Modal from "../../../components/Modal";
import Button2 from "../../../components/elements/forms/Button2";
import { toast } from "sonner";
import {
  manualTransferService,
  consoleService,
} from "../../../services/api/settings";
import { X, Loader2, Copy, Check } from "lucide-react";
import StatusModal from "../../../components/Modal/StatusModal";
import { useNavigate } from "react-router-dom";

interface BankTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  tier: string;
  frequency: "MONTHLY" | "QUARTERLY" | "YEARLY";
  featureIds?: string[];
  directPlan?: {
    tier: "STARTER" | "GROWTH" | "SCALE";
    includeSetupFee: boolean;
  };
  directAddOnIds?: string[];
  amount?: number;
  deploymentIds?: string[];
  integrationIds?: string[];
  isConsolePlan?: boolean;
  planId?: string;
}

interface BankTransferFormData {
  receipt: File | null;
}

interface FormErrors {
  receipt?: string;
}

const BankTransferModal: React.FC<BankTransferModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  tier,
  frequency,
  featureIds = [],
  directPlan,
  directAddOnIds = [],
  amount,
  deploymentIds = [],
  integrationIds = [],
  isConsolePlan = false,
  planId,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [amountInNaira, setAmountInNaira] = useState<number | null>(null);
  const [generatedRef, setGeneratedRef] = useState<string>(
    `25TH-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
  );
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState<BankTransferFormData>({
    receipt: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [statusModal, setStatusModal] = useState<{
    isOpen: boolean;
    status: "success" | "error";
    message: string;
  }>({
    isOpen: false,
    status: "success",
    message: "",
  });

  // Track previous isOpen state to detect when modal opens
  const prevIsOpenRef = useRef(false);

  // Prepare manual transfer when modal opens (not on every dependency change)
  useEffect(() => {
    // Only call API when modal transitions from closed to open
    if (
      isOpen &&
      !prevIsOpenRef.current &&
      organizationId &&
      tier &&
      !isConsolePlan
    ) {
      prepareManualTransfer();
    }

    // Update the ref to current state
    prevIsOpenRef.current = isOpen;

    // Reset form and amount when modal closes
    if (!isOpen) {
      setAmountInNaira(null);
      setFormData({ receipt: null });
      setErrors({});
      setGeneratedRef(
        `25TH-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
      );
      setCopied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const prepareManualTransfer = async () => {
    try {
      setPreparing(true);
      let response;
      if (isConsolePlan) {
        response = await consoleService.prepareManualTransfer({
          organizationId,
          planId: planId || tier,
          frequency,
          integrationIds,
          deploymentCostIds: deploymentIds,
        });
      } else {
        console.log("Preparing manual transfer for tier:", tier);
        console.log("Preparing manual transfer for featureIds:", featureIds);
        console.log("Preparing manual transfer for directPlan:", directPlan);
        console.log(
          "Preparing manual transfer for directFeatureIds:",
          directAddOnIds,
        );
        console.log("Preparing manual transfer for amount:", amount);
        console.log(
          "Preparing manual transfer for deploymentIds:",
          deploymentIds,
        );
        console.log(
          "Preparing manual transfer for integrationIds:",
          integrationIds,
        );
        response = await manualTransferService.prepare({
          tier,
          organizationId,
          frequency,
          featureIds: featureIds.length > 0 ? featureIds : undefined,
          directPlan,
          directFeatureIds:
            directAddOnIds.length > 0 ? directAddOnIds : undefined,
          amount,
        });
      }

      if (response.status === "success" && response.data) {
        setAmountInNaira(response.data.amountInNaira);
        toast.success(
          response.message || "Payment details prepared successfully",
        );
      }
    } catch (error) {
      console.error("Error preparing manual transfer:", error);
      toast.error("Failed to prepare payment details. Please try again.");
    } finally {
      setPreparing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    // Validate file type and size
    if (file) {
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          receipt: "Please upload a valid file (JPG, PNG, or PDF)",
        }));
        return;
      }

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          receipt: "File size must be less than 5MB",
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      receipt: file,
    }));

    if (errors.receipt) {
      setErrors((prev) => ({
        ...prev,
        receipt: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.receipt) {
      newErrors.receipt = "Payment receipt is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    if (!formData.receipt) {
      toast.error("Please upload a payment receipt");
      return;
    }

    setLoading(true);

    try {
      let response;
      if (isConsolePlan) {
        response = await consoleService.submitReceipt(
          formData.receipt,
          organizationId,
          planId || tier,
          frequency,
          generatedRef,
        );
      } else {
        response = await manualTransferService.submitReceipt(
          formData.receipt,
          organizationId,
          tier,
          frequency,
          featureIds,
          directPlan,
          directAddOnIds,
          amount,
          generatedRef,
        );
      }

      if (response.status === "success") {
        // Reset form
        setFormData({
          receipt: null,
        });
        setErrors({});
        // onClose(); // We'll let the StatusModal action handle the close or keep it above
        setStatusModal({
          isOpen: true,
          status: "success",
          message: response.message || "Payment receipt submitted!",
        });
      }
    } catch (error: any) {
      console.error("Payment receipt submission error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit payment receipt";
      setStatusModal({
        isOpen: true,
        status: "error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      receipt: null,
    });
    setErrors({});
    setAmountInNaira(null);
    onClose();
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedRef);
    setCopied(true);
    toast.success("Reference copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleCancel} closeOnOverlayClick={false}>
        <div className="font-abel">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b">
            <h2 className="text-[24px] font-bold text-[#212121]">
              Bank Transfer Details
            </h2>
            <button
              onClick={handleCancel}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Bank Account Information */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-bold text-[16px] mb-2 text-[#212121]">
              Transfer to this account:
            </h3>
            <div className="space-y-1 text-[14px] text-gray-700">
              <p>
                <strong>Bank Name:</strong> First Bank of Nigeria
              </p>
              <p>
                <strong>Account Name:</strong> 25th & Staffing Limited
              </p>
              <p>
                <strong>Account Number:</strong> 2028414197
              </p>
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-gray-600 mb-1 text-xs">
                  Payment Reference (Add to narration)
                </p>
                <div className="flex items-center justify-between bg-white p-2 rounded border border-blue-100">
                  <span className="font-mono font-bold text-blue-700">
                    {generatedRef}
                  </span>
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="text-blue-500 hover:text-blue-700 transition-colors"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
              {preparing ? (
                <div className="flex items-center gap-2 mt-3">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  <span className="text-blue-600">
                    Preparing payment details...
                  </span>
                </div>
              ) : amountInNaira !== null || amount ? (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="font-bold text-lg text-[#0B60D6]">
                    Amount to Send: ₦
                    {(amountInNaira || amount || 0).toLocaleString()}
                  </p>
                </div>
              ) : null}
            </div>
          </div>

          {/* Form */}
          {preparing ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-gray-600">Preparing payment details...</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* File Upload */}
              <div className="mb-3">
                <label className="font-[600] block text-[#1E1E1E] text-[15px] mb-1">
                  Payment Receipt <span className="text-[red]">*</span>
                </label>
                <input
                  type="file"
                  name="receipt"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handleFileChange}
                  className="w-full text-[14px] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Accepted formats: JPG, PNG, PDF (Max 5MB)
                </p>
                {errors.receipt && (
                  <p className="text-red-500 text-xs mt-1">{errors.receipt}</p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button2
                  title="Cancel"
                  onSubmitHandler={handleCancel}
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-100"
                  disabled={loading || preparing}
                />
                <button
                  type="submit"
                  disabled={loading || preparing || (!amountInNaira && !amount)}
                  className={`flex-1 bg-[#2D6EEF] text-white hover:bg-[#1E5DD8] border-none flex items-center justify-center text-[14px] md:text-[16px] font-abel py-3 px-4 rounded-6 h-[40px] md:h-[55px] font-[400] cursor-pointer ${
                    loading || preparing || (!amountInNaira && !amount)
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {loading ? "Submitting..." : "Submit Receipt"}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      <StatusModal
        isOpen={statusModal.isOpen}
        status={statusModal.status}
        message={statusModal.message}
        onClose={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
        onAction={
          statusModal.status === "success"
            ? () => {
                onClose(); // Close the bank transfer modal
                navigate("/"); // Or to a success dashboard
              }
            : undefined
        }
      />
    </>
  );
};

export default BankTransferModal;
